import React from 'react';
import {
    BasicForm,
    convertMBtoBytes,
    IconTextButton,
    notification,
    SelectFormItem,
    showInformationDialog,
    SimpleFileUploadItem,
    TextAreaFieldItem,
    Typography,
    useForm,
} from 'ui-lib';
import { useUploadFilesStore } from 'store';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { useSimpleFileUploadProps } from 'ui-lib/components/forms/items/use-simple-file-upload-item';
import { getErrorMessage } from 'common-utils';
import { confirmAction } from 'pages/private/admin/utils';
import {
    ARTIFACT_ALLOWED_EXTENSIONS,
    ARTIFACT_UPLOAD_FILE_TYPE_LABELS,
    ARTIFACT_UPLOAD_FILE_TYPES,
    ArtifactUploadFileType,
} from 'dto/upload-file.dto';
import { OrganisationSelectItem } from 'pages/private/components/organisation-select';
import useAccountSelector from '../hooks/use-account-selector';
import { useRaptorArtifactNavigator } from '../hooks/use-raptor-artifact-navigator';
import css from './artifacts.css';

const ARTIFACT_TYPES_SOURCE = ARTIFACT_UPLOAD_FILE_TYPES.map((type) => ({
    value: type,
    label: ARTIFACT_UPLOAD_FILE_TYPE_LABELS[type],
}));

export const UploadArtifactPage = () => {
    const [form] = useForm();
    const uploadFilesStore = useUploadFilesStore();
    const accountSelector = useAccountSelector();
    const navigation = useRaptorArtifactNavigator();
    const [selectedType, setSelectedType] = React.useState<ArtifactUploadFileType>();
    const [description, setDescription] = React.useState('');
    const { saving, withSaving } = useLoadingStates();
    const simpleFileUploadProps = useSimpleFileUploadProps({
        multiple: false,
        limitFileSize: convertMBtoBytes(__LIMIT_PACKAGE_FILE_SIZE),
        allowedFileTypes: selectedType ? ARTIFACT_ALLOWED_EXTENSIONS[selectedType] : [],
        uploadButton: { label: 'Select File To Upload', type: 'primary' },
    });

    const onTypeChange = (type: ArtifactUploadFileType) => {
        setSelectedType(type);
        // The selected file was validated against the previous type
        simpleFileUploadProps.setFiles([]);
    };

    // The account field is hidden for non-DPU users, so keep it in sync once the selected account resolves
    React.useEffect(() => {
        form.setFieldsValue({ account: accountSelector.selectedAccount?.id });
    }, [accountSelector.selectedAccount]);

    const toListingPage = () =>
        navigation.toArtifactListingPage({ accountUuid: accountSelector.selectedAccount?.uuid });

    const uploadArtifact = async () => {
        try {
            if (!accountSelector.selectedAccount) {
                return notification.error({
                    message: 'Please select an account',
                });
            }
            if (!selectedType || simpleFileUploadProps.files.length === 0) {
                return showInformationDialog({
                    modalType: 'error',
                    title: 'Upload Artifact Error',
                    content: `please choose artifact type and file`,
                });
            }
            const result = await uploadFilesStore.uploadArtifact(
                accountSelector.selectedAccount.id,
                {
                    description: description,
                    type: selectedType,
                },
                simpleFileUploadProps.files[0],
            );

            if (result) {
                notification.success({
                    message: 'Artifact Successfully Uploaded',
                    description: `Artifact ${result.fileName} has been uploaded.`,
                });
                return toListingPage();
            }

            simpleFileUploadProps.setFiles([]);
        } catch (err: any) {
            notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const handleSubmit = (values: any) => {
        confirmAction(values, 'Upload Artifact', 'Do you want to upload this artifact?', () => withSaving(uploadArtifact));
    };

    return (
        <React.Fragment>
            <BasicForm
                title='Upload New Artifact'
                form={form}
                onSubmit={handleSubmit}
                className={css['upload-form']}
                items={[
                    <OrganisationSelectItem
                        key='account'
                        code='account'
                        label='Account'
                        colon={true}
                        labelAlign='right'
                        initialValue={accountSelector.selectedAccount}
                        hidden={!accountSelector.isDPUAdmin}
                        isRequired={true}
                        selectProps={{
                            treeDataSource: accountSelector.dataSource,
                            expandAll: true,
                            value: accountSelector.selectedAccount?.id,
                            onChange: accountSelector.onChange,
                        }}
                    />,
                    <SelectFormItem
                        key='type'
                        code='type'
                        label='Type'
                        colon={true}
                        isRequired={true}
                        labelAlign='right'
                        placeholder='Select artifact type'
                        dataSource={ARTIFACT_TYPES_SOURCE}
                        onChange={onTypeChange}
                    />,
                    <TextAreaFieldItem
                        initialValue={description}
                        key='description'
                        code='description'
                        label='Description'
                        colon={true}
                        isRequired={false}
                        labelAlign='right'
                        textAreaProps={{
                            autoSize: { minRows: 3, maxRows: 6 },
                            onChange: (event) => setDescription(event.target.value),
                        }}
                    />,
                    <SimpleFileUploadItem
                        key='fileUpload'
                        simpleFileUploadProps={{
                            ...simpleFileUploadProps.getFileUploadProps(),
                            disabled: !selectedType,
                        }}
                        code='fileUpload'
                        label=' '
                    />,
                    <div key='footer' className={css['row']}>
                        <IconTextButton
                            key='back-button'
                            className={css['footer-left-button']}
                            htmlType='button'
                            label='Back'
                            disabled={saving}
                            onClick={toListingPage}
                        />
                        <IconTextButton
                            key='upload-submit-button'
                            className={css['footer-right-button']}
                            htmlType='submit'
                            label='Upload'
                            type='primary'
                            loading={saving}
                            disabled={
                                saving ||
                                !selectedType ||
                                !simpleFileUploadProps.files.length ||
                                simpleFileUploadProps.hasFileError
                            }
                        />
                    </div>,
                ]}
            />
        </React.Fragment>
    );
};
